import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Mail, Phone, MapPin, CheckCircle2 } from "lucide-react";
import { PageHero } from "@/components/common/PageHero";
import { api } from "@/lib/api";
import { CONTACT } from "@/lib/content";

const EMPTY = {
  name: "", company: "", email: "", country: "", job_title: "",
  company_size: "", goal: "", team_size: "", timeline: "", message: "",
};

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please share your name and work email.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Thank you — we'll be in touch shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHero
        kicker="Contact"
        title="Let's talk about your India plans."
        subtitle="Whether you're exploring India for the first time, planning your first hire, or evaluating a full GCC, we'd love to understand your goals and discuss the right path forward."
      />

      <section className="section-pad" data-testid="contact-section">
        <div className="container-px grid gap-12 lg:grid-cols-[1fr_1.6fr]">
          {/* info */}
          <div className="space-y-6">
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-7">
              <h3 className="font-head text-lg font-bold text-white">Reach us directly</h3>
              <ul className="mt-5 space-y-4 text-sm text-zinc-400">
                <li className="flex items-start gap-3"><Mail className="mt-0.5 h-4 w-4 text-orange-400" /> {CONTACT.email}</li>
                <li className="flex items-start gap-3"><Phone className="mt-0.5 h-4 w-4 text-orange-400" /> {CONTACT.phone}</li>
                <li className="flex items-start gap-3"><MapPin className="mt-0.5 h-4 w-4 text-orange-400" /> {CONTACT.address}</li>
              </ul>
            </div>
            <div className="rounded-2xl border border-orange-500/20 bg-[#17171A] p-7">
              <p className="font-head text-lg font-semibold text-white">No pressure. Just a conversation.</p>
              <p className="mt-3 text-sm text-zinc-400">We'll listen first, understand your goals, and only then suggest a path. Book a free consultation whenever you're ready.</p>
            </div>
          </div>

          {/* form / success */}
          {done ? (
            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="flex flex-col items-center justify-center rounded-[2rem] border border-orange-500/20 bg-[#17171A] p-12 text-center" data-testid="contact-success">
              <div className="flex h-16 w-16 items-center justify-center rounded-full grad-accent text-[#1a0f00]"><CheckCircle2 className="h-8 w-8" /></div>
              <h3 className="mt-6 font-head text-2xl font-bold text-white">Thank you!</h3>
              <p className="mt-3 max-w-sm text-zinc-400">Your message is on its way to our team. We'll get back to you shortly to start the conversation.</p>
              <button onClick={() => setDone(false)} className="btn-ghost mt-8" data-testid="contact-send-another">Send another message</button>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="rounded-[2rem] border border-white/10 bg-white/[0.02] p-7 sm:p-10" data-testid="contact-form">
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Name *" value={form.name} onChange={set("name")} testid="contact-name" />
                <Field label="Company" value={form.company} onChange={set("company")} testid="contact-company" />
                <Field label="Work Email *" type="email" value={form.email} onChange={set("email")} testid="contact-email" />
                <Field label="Country" value={form.country} onChange={set("country")} testid="contact-country" />
                <Field label="Job Title" value={form.job_title} onChange={set("job_title")} testid="contact-job-title" />
                <SelectField label="Company Size" value={form.company_size} onChange={set("company_size")} testid="contact-company-size"
                  options={["1–10", "11–50", "51–200", "201–500", "500+"]} />
                <Field label="Expected Team Size in India" value={form.team_size} onChange={set("team_size")} testid="contact-team-size" />
                <SelectField label="Expected Timeline" value={form.timeline} onChange={set("timeline")} testid="contact-timeline"
                  options={["Within 30–60 days", "3–6 months", "6–12 months", "Just exploring"]} />
              </div>
              <div className="mt-5">
                <Field label="What are you looking to build in India?" value={form.goal} onChange={set("goal")} testid="contact-goal" />
              </div>
              <div className="mt-5">
                <label className="mb-2 block text-sm text-zinc-400">Message</label>
                <textarea rows={4} value={form.message} onChange={set("message")} data-testid="contact-message"
                  className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white outline-none transition-colors focus:border-orange-500/50" />
              </div>
              <button type="submit" disabled={loading} className="btn-primary mt-8 w-full justify-center disabled:opacity-60" data-testid="contact-submit">
                {loading ? "Sending…" : "Start the Conversation"} <ArrowRight className="h-4 w-4" />
              </button>
            </form>
          )}
        </div>
      </section>
    </>
  );
}

const Field = ({ label, value, onChange, type = "text", testid }) => (
  <div>
    <label className="mb-2 block text-sm text-zinc-400">{label}</label>
    <input type={type} value={value} onChange={onChange} data-testid={testid}
      className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white outline-none transition-colors focus:border-orange-500/50" />
  </div>
);

const SelectField = ({ label, value, onChange, options, testid }) => (
  <div>
    <label className="mb-2 block text-sm text-zinc-400">{label}</label>
    <select value={value} onChange={onChange} data-testid={testid}
      className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white outline-none transition-colors focus:border-orange-500/50">
      <option value="" className="bg-[#17171A]">Select…</option>
      {options.map((o) => <option key={o} value={o} className="bg-[#17171A]">{o}</option>)}
    </select>
  </div>
);
